import { useEffect, useRef, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { useAppSelector } from "app/hooks";
import Dialog from "components/dialog";

import { selectShowMedicalScribeSilenceWarningDialog } from "../medical-scribe.slice";

export const AMBIENT_SCRIBE_SILENCE_DETECTION_SETTINGS = {
  silence_detection_sec: 1800,
  silence_grace_period_sec: 60,
  aggressiveness: 3,
  speech_ratio_threshold: 0.1,
  energy_margin_db: 8.0,
  noise_floor_percentile: 15.0
};

type MedicalScribeSilenceWarningDialogProps = {
  onContinue: () => void;
  onClose: () => void;
  onPause: () => void;
};
const dialogContent = {
  sx: {
    padding: "20px 40px !important",
    minWidth: "320px",
    maxWidth: "320px"
  }
};

const INITIAL_COUNTDOWN =
  AMBIENT_SCRIBE_SILENCE_DETECTION_SETTINGS.silence_grace_period_sec - 1;

export default function MedicalScribeSilenceWarningDialog({
  onContinue,
  onClose,
  onPause
}: MedicalScribeSilenceWarningDialogProps) {
  const open = useAppSelector(selectShowMedicalScribeSilenceWarningDialog);

  const [elapsedSeconds, setElapsedSeconds] = useState(INITIAL_COUNTDOWN);

  // Keep callbacks in refs to avoid dependency issues
  const onCloseRef = useRef(onClose);
  const onPauseRef = useRef(onPause);
  useEffect(() => {
    onCloseRef.current = onClose;
    onPauseRef.current = onPause;
  }, [onClose, onPause]);

  // Handler for "Yes" button - closes dialog and pauses recording
  const handleYesClick = () => {
    onClose();
    onPause();
  };

  // Reset countdown and start timer when dialog opens
  useEffect(() => {
    if (!open) {
      return;
    }

    // Reset to initial value when dialog opens
    setElapsedSeconds(INITIAL_COUNTDOWN);

    const interval = setInterval(() => {
      setElapsedSeconds((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(interval);
          // Defer callbacks to avoid calling during state update
          setTimeout(() => {
            onCloseRef.current();
            onPauseRef.current();
          }, 0);
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  return (
    <Dialog
      title="Silence detected"
      onClose={onClose}
      open={Boolean(open)}
      minWidth={480}
      maxWidth="lg"
      dialogContentProps={dialogContent}
    >
      <Stack width="100%" direction="column" rowGap={2} sx={{ mb: 2 }}>
        <Typography variant="body3" fontWeight={700}>
          Pause recording?
        </Typography>
      </Stack>

      <DialogActions>
        <Stack direction="row" gap={3}>
          <Button variant="outlined" onClick={handleYesClick} size="small">
            Yes ({elapsedSeconds})
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={onContinue}
            size="small"
          >
            No
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
